/**
 * revealPageText.js — REVEAL-001 C2.
 *
 * Pure helpers that turn the RAW pdfjs text runs of ONE reveal page into a
 * structured reading model the mobile reading card renders at >=16px (the
 * constitutional mobile-body-size gate). The card's text + page splits DERIVE
 * from the real RemnantBookPDF render (pdfjs text layer) — never a parallel
 * re-pagination — so the card cannot silently diverge from what prints. A
 * failing-capable parity test (revealReadingCardParity.test.js) asserts the card
 * renders EXACTLY each page's extracted text (whitespace-normalized), using THIS
 * same helper, so any drift breaks CI instead of breaking trust.
 *
 * Kept here (not inside the react-pdf-importing RevealPages component) so it has a
 * real unit test — RevealPages itself can't load in CRA Jest (react-pdf ESM).
 */

// Two runs share a line when their baselines are within this fraction of the
// page's median glyph height.
const LINE_TOL_FRAC = 0.6;
// A vertical gap between lines larger than (median line gap * this) starts a new
// paragraph block.
const PARA_GAP_FACTOR = 1.5;
// Font-size buckets relative to the page's median body size.
const HEADING_FACTOR = 1.25; // >= median * this  → heading (chapter title)
const EYEBROW_FACTOR = 0.85; // <= median * this  → eyebrow (kicker / attribution)

const median = (nums) => {
  if (!nums.length) return 0;
  const s = [...nums].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

// Two runs are treated as ONE word only when they physically touch: the previous run's advance lands on
// the next run's origin. Intra-word splits (kerning/ligature boundaries) measure ~0 or negative; a real
// word-space measures far wider. Expressed in em so it is font- and size-independent.
const CONTIGUOUS_EPS_EM = 0.02;

// Join the runs of a single line into a string. pdfjs runs on a line are laid out left→right.
//
// F-207 — FAIL-CLOSED FOR TEXT FIDELITY (constitution §6 applied to a teller's own words).
// A space is now the DEFAULT between two runs; it is suppressed ONLY on proven contiguity. The previous
// rule was the opposite — it required proof of SEPARATION (`gap > fontSize * 0.25`) and defaulted to
// dropping the space — and that default silently destroyed real spaces on production:
//   measured on a real render, a Lora word-space at 13pt advances 2.851pt = 21.9% of an em, which is
//   NARROWER than the 25% threshold it had to clear, so where the loss occurs it is not marginal.
// MEASURED SCOPE (do not overstate — an earlier diagnosis claimed this fired at EVERY bold→regular
// boundary on every story; the corpus refuted that): across 12 real saved stories the old rule dropped a
// real space on 1 story. The loss is CONTINGENT on whether pdfjs encodes that particular space as a
// character (then `curStartsSpace` already saves it) or positionally (then only the gap test can). So it
// is at most one occurrence per story — the bold opener is the only bold→regular boundary — and it hits
// a fraction of stories. Live symptom: "morning,singing" on the mobile reveal; corpus find:
// "days beforeChrist". RemnantBookPDF.js:582 cuts the bold opener at /[,;.!?—]/, which is why the
// symptom usually presents as glued punctuation, but a cut on an unpunctuated word glues two words.
// Note pdfjs supplies a true `width` here, so estWidth's estimate never fired; the threshold itself was
// the defect. A tuned constant would still encode one font at one size, so the DEFAULT is inverted
// instead: dropping a space now requires positive evidence, and the failure mode flips from invisible
// (a missing space inside the teller's sentence) to visible and test-catchable.
const joinLine = (runs) => {
  const ordered = [...runs].sort((a, b) => a.x - b.x);
  let out = '';
  for (let i = 0; i < ordered.length; i++) {
    const cur = ordered[i];
    if (i > 0) {
      const prev = ordered[i - 1];
      const gap = cur.x - (prev.x + estWidth(prev));
      const prevEndsSpace = /\s$/.test(out);
      const curStartsSpace = /^\s/.test(cur.str);
      const touching = gap <= cur.fontSize * CONTIGUOUS_EPS_EM;
      if (!prevEndsSpace && !curStartsSpace && !touching) out += ' ';
    }
    out += cur.str;
  }
  return out.replace(/\s+/g, ' ').trim();
};

// pdfjs text items don't always carry a width; approximate from glyph count so
// the inter-run gap test has something to work with.
const estWidth = (run) =>
  typeof run.width === 'number' && run.width > 0 ? run.width : run.str.length * run.fontSize * 0.5;

/**
 * buildReadingModel(runs) → { blocks: [{ kind, text, fontSize }] }
 *   kind ∈ 'heading' | 'eyebrow' | 'body', top-to-bottom reading order.
 *   runs: [{ str, x, y, fontSize, width? }] from one page (pdfjs text layer).
 */
function buildReadingModel(runs) {
  const clean = (runs || []).filter((r) => r && typeof r.str === 'string' && r.str.trim());
  if (!clean.length) return { blocks: [] };

  const medFont = median(clean.map((r) => r.fontSize || 0)) || 12;
  const lineTol = medFont * LINE_TOL_FRAC;

  // Cluster runs into lines by baseline y (pdfjs y-origin is bottom-left).
  const byY = [...clean].sort((a, b) => b.y - a.y);
  const lines = [];
  for (const run of byY) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(last.y - run.y) <= lineTol) {
      last.runs.push(run);
      last.y = (last.y * (last.runs.length - 1) + run.y) / last.runs.length;
    } else {
      lines.push({ y: run.y, runs: [run] });
    }
  }

  const lineModels = lines.map((ln) => ({
    y: ln.y,
    text: joinLine(ln.runs),
    maxFont: Math.max(...ln.runs.map((r) => r.fontSize || 0)),
  })).filter((ln) => ln.text);

  if (!lineModels.length) return { blocks: [] };

  // Median gap between consecutive lines → paragraph-break threshold.
  const gaps = [];
  for (let i = 1; i < lineModels.length; i++) gaps.push(lineModels[i - 1].y - lineModels[i].y);
  const medGap = median(gaps.filter((g) => g > 0)) || medFont * 1.5;
  const paraGapThreshold = medGap * PARA_GAP_FACTOR;

  // Group lines into blocks; break on a large gap OR a heading/body font change.
  const blocks = [];
  let cur = null;
  for (let i = 0; i < lineModels.length; i++) {
    const ln = lineModels[i];
    const gap = i > 0 ? lineModels[i - 1].y - ln.y : 0;
    const fontJumped = cur && Math.abs(ln.maxFont - cur.maxFont) > medFont * 0.2;
    if (!cur || gap > paraGapThreshold || fontJumped) {
      cur = { lines: [ln.text], maxFont: ln.maxFont };
      blocks.push(cur);
    } else {
      cur.lines.push(ln.text);
      cur.maxFont = Math.max(cur.maxFont, ln.maxFont);
    }
  }

  return {
    blocks: blocks.map((b) => ({
      kind:
        b.maxFont >= medFont * HEADING_FACTOR ? 'heading'
          : b.maxFont <= medFont * EYEBROW_FACTOR ? 'eyebrow'
            : 'body',
      text: b.lines.join(' ').replace(/\s+/g, ' ').trim(),
      fontSize: b.maxFont,
    })),
  };
}

/**
 * Split a body opener into a bold lead-in + the rest — mirrors the print
 * BoldOpenerParagraph (RemnantBookPDF.js) so the reading card's first paragraph
 * on page 1 opens the same way the printed page does.
 */
function boldOpenerSplit(text) {
  if (!text || text.length < 2) return { bold: text || '', rest: '' };
  const words = text.split(' ');
  const boldCount =
    Math.min(Math.max(5, words.findIndex((w, i) => i >= 4 && /[,;.!?—]/.test(w)) + 1), 8) || 6;
  return { bold: words.slice(0, boldCount).join(' '), rest: words.slice(boldCount).join(' ') };
}

// Whitespace-normalized concatenation of a reading model's text — the exact
// value the parity test compares against the page's extracted PDF text.
function modelPlainText(model) {
  return (model.blocks || []).map((b) => b.text).join(' ').replace(/\s+/g, ' ').trim();
}

module.exports = { buildReadingModel, boldOpenerSplit, modelPlainText };
